import { useState, useEffect, useCallback, useRef } from 'react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { formulationsAPI, clientsAPI, productsAPI } from '../lib/api';
import { formatDate, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Beaker, 
  Plus,
  User,
  FlaskConical,
  Loader2,
  Play,
  Pencil,
  History as HistoryIcon,
  Trash2,
  ScanBarcode,
  Package,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import SearchableSelect from '../components/SearchableSelect';

export default function FormulacionesPage() {
  const { selectedBusiness } = useAuth();
  const [formulations, setFormulations] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFormulation, setEditingFormulation] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  
  const [formData, setFormData] = useState({
    client_id: '',
    name: '',
    description: '',
    ingredients: [],
    notes: ''
  });

  const [newIngredient, setNewIngredient] = useState({ product_id: '', quantity: 1, use_type: 'gramaje' });
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanMode, setScanMode] = useState(false);
  const barcodeInputRef = useRef(null);

  // Get selected product info for ingredient form
  const selectedIngredientProduct = products.find(p => p.id === newIngredient.product_id);

  const loadData = useCallback(async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const [formulationsRes, clientsRes, productsRes] = await Promise.all([
        formulationsAPI.getAll(selectedClient?.id),
        clientsAPI.getAll({ limit: 5000 }),
        productsAPI.getAll({ is_insumo: true })
      ]);
      
      setFormulations(formulationsRes.data);
      setClients(clientsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      logger.error('Error loading formulations:', error);
      toast.error('Error al cargar formulaciones');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, selectedClient]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openNewFormulation = (client = null) => {
    setEditingFormulation(null);
    setFormData({
      client_id: client?.id || '',
      name: '',
      description: '',
      ingredients: [],
      notes: ''
    });
    setDialogOpen(true);
  };

  const openEditFormulation = (formula) => {
    setEditingFormulation(formula);
    // Rehydrate each ingredient with the runtime fields the UI expects
    // (product_name, unit, available, use_type, has_content_tracking)
    const ings = (formula.ingredients || []).map((ing) => {
      const p = products.find((pr) => pr.id === ing.product_id);
      const hasContent = !!p?.total_content;
      const useType = ing.use_type || (hasContent ? 'gramaje' : 'unidad');
      const available = useType === 'unidad'
        ? p?.stock ?? 0
        : (hasContent ? (p?.current_content ?? p?.total_content ?? 0) : p?.stock ?? 0);
      return {
        product_id: ing.product_id,
        product_name: ing.product_name || p?.name || 'Producto',
        quantity: ing.quantity,
        unit: ing.unit || (useType === 'unidad' ? p?.unit : p?.content_unit) || 'unidad',
        available,
        use_type: useType,
        has_content_tracking: hasContent,
      };
    });
    setFormData({
      client_id: formula.client_id || '',
      name: formula.name || '',
      description: formula.description || '',
      ingredients: ings,
      notes: formula.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDeleteFormulation = async (formula) => {
    if (!window.confirm(`¿Eliminar la formulación "${formula.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await formulationsAPI.delete(formula.id);
      toast.success('Formulación eliminada');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const addIngredient = () => {
    if (!newIngredient.product_id || newIngredient.quantity <= 0) return;
    
    const product = products.find(p => p.id === newIngredient.product_id);
    if (!product) return;
    
    // Determine if using gramaje or full unit
    const hasContentTracking = !!product.total_content;
    const useFullUnit = newIngredient.use_type === 'unidad';
    
    let quantity, unit, available;
    
    if (useFullUnit) {
      // Using complete unit(s)
      quantity = newIngredient.quantity;
      unit = product.unit || 'unidad';
      available = product.stock;
    } else if (hasContentTracking) {
      // Using gramaje/content
      quantity = newIngredient.quantity;
      unit = product.content_unit || 'gr';
      available = product.current_content ?? product.total_content;
    } else {
      // Product without content tracking - use stock
      quantity = newIngredient.quantity;
      unit = product.unit || 'unidad';
      available = product.stock;
    }
    
    setFormData({
      ...formData,
      ingredients: [
        ...formData.ingredients,
        {
          product_id: newIngredient.product_id,
          product_name: product.name,
          quantity: quantity,
          unit: unit,
          available: available,
          use_type: useFullUnit ? 'unidad' : 'gramaje',
          has_content_tracking: hasContentTracking
        }
      ]
    });
    setNewIngredient({ product_id: '', quantity: 1, use_type: 'gramaje' });
    setBarcodeInput('');
  };

  const removeIngredient = (index) => {
    const updated = [...formData.ingredients];
    updated.splice(index, 1);
    setFormData({ ...formData, ingredients: updated });
  };

  const handleBarcodeSearch = async (barcode) => {
    if (!barcode.trim()) return;
    
    try {
      const response = await productsAPI.getByBarcode(barcode.trim());
      const product = response.data;
      
      if (product) {
        setNewIngredient({ product_id: product.id, quantity: 1, use_type: product.total_content ? 'gramaje' : 'unidad' });
        toast.success(`Producto encontrado: ${product.name}`);
      } else {
        toast.error('Producto no encontrado con ese código');
      }
    } catch (error) {
      toast.error('Producto no encontrado con ese código de barras');
    }
    setBarcodeInput('');
  };

  const handleBarcodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeSearch(barcodeInput);
    }
  };

  const handleSaveFormulation = async () => {
    if (!formData.client_id || !formData.name || formData.ingredients.length === 0) {
      toast.error('Cliente, nombre e ingredientes son requeridos');
      return;
    }

    // Persist only the fields the backend needs; drop UI-only ones.
    const cleanIngredients = formData.ingredients.map((ing) => ({
      product_id: ing.product_id,
      product_name: ing.product_name,
      quantity: ing.quantity,
      unit: ing.unit,
      use_type: ing.use_type,
    }));

    try {
      if (editingFormulation) {
        await formulationsAPI.update(editingFormulation.id, {
          client_id: formData.client_id,
          name: formData.name,
          description: formData.description,
          ingredients: cleanIngredients,
          notes: formData.notes,
        });
        toast.success('Formulación actualizada');
      } else {
        await formulationsAPI.create({
          ...formData,
          ingredients: cleanIngredients,
          business_id: selectedBusiness.id,
        });
        toast.success('Formulación guardada');
      }
      setDialogOpen(false);
      setEditingFormulation(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleApplyFormulation = async (formula) => {
    if (!window.confirm('¿Aplicar esta formulación? Se descontarán los insumos del inventario.')) return;

    try {
      await formulationsAPI.apply(formula.id);
      toast.success('Formulación aplicada, inventario actualizado');
      loadData();
    } catch (error) {
      toast.error('Error al aplicar formulación');
    }
  };

  const handleOpenHistory = async (formula) => {
    setHistoryData({ formula, applications: [] });
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const { data } = await formulationsAPI.history(formula.id);
      setHistoryData(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo cargar el historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  const getClient = (clientId) => clients.find(c => c.id === clientId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  // Group formulations by client
  const formulationsByClient = formulations.reduce((acc, f) => {
    if (!acc[f.client_id]) {
      acc[f.client_id] = {
        client: getClient(f.client_id),
        formulas: []
      };
    }
    acc[f.client_id].formulas.push(f);
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in" data-testid="formulaciones-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Formulaciones
          </h1>
          <p className="text-slate-500 mt-1">
            Recetas de tratamientos personalizados por cliente
          </p>
        </div>
        <Button 
          onClick={() => openNewFormulation()}
          className="bg-primary text-white hover:bg-primary/90"
          data-testid="new-formulation-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva formulación
        </Button>
      </div>

      {/* Client Filter */}
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label className="text-slate-500">Filtrar por cliente</Label>
            <div className="w-72">
              <SearchableSelect
                items={[{ id: 'all', first_name: 'Todos', last_name: 'los clientes', _all: true }, ...clients]}
                value={selectedClient?.id || 'all'}
                onChange={(item) => setSelectedClient(item && !item._all ? item : null)}
                getLabel={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Sin nombre'}
                getSubLabel={(c) => c._all ? '' : (c.phone || c.email || '')}
                searchFields={(c) => [c.first_name, c.last_name, c.phone, c.email].filter(Boolean)}
                placeholder="Todos los clientes"
                emptyLabel="Sin coincidencias"
                allowClear={false}
                testId="formulations-client-filter"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Formulations */}
      {Object.keys(formulationsByClient).length === 0 ? (
        <Card className="border-slate-200 bg-white">
          <CardContent className="flex flex-col items-center justify-center h-64 text-slate-500">
            <FlaskConical className="w-12 h-12 mb-3 opacity-50" />
            <p>No hay formulaciones registradas</p>
            <Button variant="link" onClick={() => openNewFormulation()} className="mt-2">
              Crear primera formulación
            </Button>
          </CardContent>
        </Card>
      ) : (
        Object.entries(formulationsByClient).map(([clientId, data]) => (
          <Card key={clientId} className="border-slate-200 bg-white">
            <CardHeader className="border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-slate-200 text-white">
                      {data.client?.first_name?.charAt(0)}{data.client?.last_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                      {data.client?.first_name} {data.client?.last_name}
                    </CardTitle>
                    <p className="text-sm text-slate-500">
                      {data.formulas.length} formulación(es)
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-slate-200"
                  onClick={() => openNewFormulation(data.client)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Nueva
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.formulas.map((formula, index) => (
                  <div 
                    key={formula.id}
                    className="p-4 rounded-lg bg-slate-50 animate-fade-in"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold">{formula.name}</h4>
                        <p className="text-xs text-slate-500">{formatDate(formula.created_at)}</p>
                        {formula.last_applied_at && (
                          <p className="text-[11px] text-emerald-600 mt-0.5" data-testid={`last-applied-${formula.id}`}>
                            Última aplicación: {formatDate(formula.last_applied_at)}
                            {formula.applied_count > 0 && ` · ${formula.applied_count}x`}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="h-8 w-8 border-slate-300 hover:bg-slate-100"
                          onClick={() => openEditFormulation(formula)}
                          title="Editar formulación"
                          data-testid={`edit-formulation-${formula.id}`}
                        >
                          <Pencil className="w-4 h-4 text-slate-600" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="h-8 w-8 border-slate-300 hover:bg-slate-100"
                          onClick={() => handleOpenHistory(formula)}
                          title="Ver historial de aplicaciones"
                          data-testid={`history-formulation-${formula.id}`}
                        >
                          <HistoryIcon className="w-4 h-4 text-slate-600" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="h-8 w-8 border-emerald-500/30 hover:bg-emerald-500/20"
                          onClick={() => handleApplyFormulation(formula)}
                          title="Aplicar formulación"
                          data-testid={`apply-formulation-${formula.id}`}
                        >
                          <Play className="w-4 h-4 text-emerald-400" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="h-8 w-8 border-red-300 hover:bg-red-50"
                          onClick={() => handleDeleteFormulation(formula)}
                          title="Eliminar formulación"
                          data-testid={`delete-formulation-${formula.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>

                    {formula.description && (
                      <p className="text-sm text-slate-500 mb-3">{formula.description}</p>
                    )}

                    <div className="space-y-2">
                      <p className="text-xs text-slate-500 font-medium">Ingredientes:</p>
                      {formula.ingredients.map((ing, i) => {
                        const product = products.find(p => p.id === ing.product_id);
                        return (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="text-zinc-300">{product?.name || 'Producto'}</span>
                            <Badge variant="outline" className="text-slate-500 border-slate-300">
                              {ing.quantity} {ing.unit || product?.unit}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>

                    {formula.notes && (
                      <p className="mt-3 text-xs text-slate-500 italic">{formula.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* New / Edit Formulation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => {
        setDialogOpen(v);
        if (!v) setEditingFormulation(null);
      }}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[85vh] overflow-hidden p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editingFormulation ? 'Editar formulación' : 'Nueva formulación'}
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto max-h-[calc(85vh-140px)] px-6">
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <SearchableSelect
                  items={clients}
                  value={formData.client_id || ''}
                  onChange={(c) => setFormData({ ...formData, client_id: c ? c.id : '' })}
                  getLabel={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Sin nombre'}
                  getSubLabel={(c) => c.phone || c.email || ''}
                  searchFields={(c) => [c.first_name, c.last_name, c.phone, c.email].filter(Boolean)}
                  placeholder="Buscar cliente por nombre, teléfono o email…"
                  emptyLabel="Sin coincidencias"
                  testId="formulation-client-select"
                />
              </div>

            <div className="space-y-2">
              <Label>Nombre de la formulación *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-slate-100 border-slate-200"
                placeholder="Ej: Tinte rubio cenizo"
              />
            </div>

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-slate-100 border-slate-200 resize-none"
                placeholder="Descripción del tratamiento..."
                rows={2}
              />
            </div>

            {/* Ingredients */}
            <div className="space-y-3">
              <Label>Ingredientes *</Label>
              
              {/* Barcode Scanner Input */}
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center gap-2 text-slate-500">
                  <ScanBarcode className="w-4 h-4" />
                  <span className="text-sm font-medium">Escanear código de barras</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    ref={barcodeInputRef}
                    type="text"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onKeyDown={handleBarcodeKeyDown}
                    className="flex-1 bg-white border-slate-300 font-mono"
                    placeholder="Escanear o escribir código de barras..."
                    autoFocus={scanMode}
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => handleBarcodeSearch(barcodeInput)}
                    className="border-slate-300"
                  >
                    Buscar
                  </Button>
                </div>
              </div>

              {/* Manual Selection */}
              <div className="text-center text-xs text-slate-500 py-1">— o seleccionar manualmente —</div>
              
              {/* Product Selection */}
              <Select 
                value={newIngredient.product_id} 
                onValueChange={(value) => {
                  const product = products.find(p => p.id === value);
                  setNewIngredient({ 
                    ...newIngredient, 
                    product_id: value,
                    use_type: product?.total_content ? 'gramaje' : 'unidad'
                  });
                }}
              >
                <SelectTrigger className="bg-slate-100 border-slate-200">
                  <SelectValue placeholder="Seleccionar insumo" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <ScrollArea className="h-48">
                    {products.map(product => {
                      const hasContent = !!product.total_content;
                      const available = hasContent 
                        ? `${product.current_content?.toFixed(1) || 0}${product.content_unit}` 
                        : `${product.stock} ${product.unit}`;
                      return (
                        <SelectItem key={product.id} value={product.id}>
                          <div className="flex justify-between items-center w-full">
                            <span>{product.name}</span>
                            <span className="text-slate-500 ml-2 text-xs">
                              {available}
                              {hasContent && ' (gramaje)'}
                            </span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </ScrollArea>
                </SelectContent>
              </Select>

              {/* Quantity and Type Selection */}
              {selectedIngredientProduct && (
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{selectedIngredientProduct.name}</span>
                    <span className="text-xs text-slate-500">
                      Stock: {selectedIngredientProduct.stock} {selectedIngredientProduct.unit}
                      {selectedIngredientProduct.total_content && (
                        <> • Contenido: {selectedIngredientProduct.current_content?.toFixed(1) || 0}/{selectedIngredientProduct.total_content}{selectedIngredientProduct.content_unit}</>
                      )}
                    </span>
                  </div>
                  
                  {/* Type selector - only show if product has content tracking */}
                  {selectedIngredientProduct.total_content && (
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={newIngredient.use_type === 'gramaje' ? 'default' : 'outline'}
                        size="sm"
                        className={cn(
                          "flex-1",
                          newIngredient.use_type === 'gramaje' 
                            ? "bg-white text-black" 
                            : "border-slate-300 text-slate-500"
                        )}
                        onClick={() => setNewIngredient({ ...newIngredient, use_type: 'gramaje', quantity: 1 })}
                      >
                        Por gramaje ({selectedIngredientProduct.content_unit})
                      </Button>
                      <Button
                        type="button"
                        variant={newIngredient.use_type === 'unidad' ? 'default' : 'outline'}
                        size="sm"
                        className={cn(
                          "flex-1",
                          newIngredient.use_type === 'unidad' 
                            ? "bg-white text-black" 
                            : "border-slate-300 text-slate-500"
                        )}
                        onClick={() => setNewIngredient({ ...newIngredient, use_type: 'unidad', quantity: 1 })}
                      >
                        Unidad completa
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex gap-2 items-end">
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs text-slate-500">
                        {newIngredient.use_type === 'unidad' 
                          ? `Cantidad (${selectedIngredientProduct.unit})` 
                          : `Cantidad (${selectedIngredientProduct.content_unit || 'gr'})`
                        }
                      </Label>
                      <Input
                        type="number"
                        value={newIngredient.quantity}
                        onChange={(e) => setNewIngredient({ ...newIngredient, quantity: parseFloat(e.target.value) || 1 })}
                        className="bg-white border-slate-300"
                        min={newIngredient.use_type === 'unidad' ? 1 : 0.1}
                        step={newIngredient.use_type === 'unidad' ? 1 : 0.1}
                      />
                    </div>
                    <Button onClick={addIngredient} className="bg-primary text-white hover:bg-primary/90">
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar
                    </Button>
                  </div>
                </div>
              )}

              {/* If no product selected, show simple add row */}
              {!selectedIngredientProduct && (
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={newIngredient.quantity}
                    onChange={(e) => setNewIngredient({ ...newIngredient, quantity: parseFloat(e.target.value) || 1 })}
                    className="w-24 bg-slate-100 border-slate-200"
                    min={0.1}
                    step={0.1}
                    placeholder="Cant."
                    disabled
                  />
                  <Button variant="outline" disabled className="border-slate-200">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {formData.ingredients.length > 0 && (
                <div className="space-y-2 mt-3 p-3 rounded-lg bg-slate-50">
                  <div className="text-xs text-slate-500 mb-2">Ingredientes agregados:</div>
                  {formData.ingredients.map((ing, i) => {
                    const isLowStock = ing.use_type === 'gramaje' && ing.quantity > (ing.available || 0);
                    return (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Package className="w-3 h-3 text-slate-500" />
                          <span className="text-sm">{ing.product_name}</span>
                          {isLowStock && (
                            <AlertCircle className="w-3 h-3 text-amber-400" title="Stock insuficiente" />
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={cn(
                            "text-slate-500",
                            isLowStock && "text-amber-400 border-amber-500/30"
                          )}>
                            {ing.quantity} {ing.unit}
                            {ing.use_type === 'unidad' && ' (completo)'}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 hover:text-red-400"
                            onClick={() => removeIngredient(i)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-slate-100 border-slate-200 resize-none"
                placeholder="Observaciones, tiempo de aplicación, etc."
                rows={2}
              />
            </div>
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleSaveFormulation} className="bg-primary text-white hover:bg-primary/90">
              {editingFormulation ? 'Guardar cambios' : 'Guardar formulación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[85vh] overflow-hidden p-0" data-testid="formulation-history-dialog">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Historial de aplicaciones
            </DialogTitle>
            {historyData?.formula && (
              <p className="text-sm text-slate-500 mt-1">
                {historyData.formula.name}
                {historyData.total_applications > 0 && (
                  <> · <strong>{historyData.total_applications}</strong> aplicación(es)</>
                )}
              </p>
            )}
          </DialogHeader>

          <div className="overflow-y-auto max-h-[calc(85vh-140px)] px-6 pb-6">
            {historyLoading ? (
              <div className="py-10 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (historyData?.applications?.length || 0) === 0 ? (
              <div className="py-10 text-center text-sm text-slate-400">
                Esta formulación aún no se ha aplicado.
              </div>
            ) : (
              <div className="border border-slate-200 rounded-md overflow-hidden mt-2">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs text-slate-500">
                      <th className="px-3 py-2 font-medium">Fecha</th>
                      <th className="px-3 py-2 font-medium">Hora</th>
                      <th className="px-3 py-2 font-medium">Aplicó</th>
                    </tr>
                  </thead>
                  <tbody data-testid="history-rows">
                    {historyData.applications.map((a) => {
                      const dt = new Date(a.applied_at);
                      const fecha = dt.toLocaleDateString('es-MX', {
                        day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Mexico_City',
                      });
                      const hora = dt.toLocaleTimeString('es-MX', {
                        hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'America/Mexico_City',
                      });
                      return (
                        <tr key={a.id} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-mono text-xs text-slate-700 whitespace-nowrap">{fecha}</td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-700 whitespace-nowrap">{hora}</td>
                          <td className="px-3 py-2 text-slate-700">{a.applied_by_name || 'Usuario'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={() => setHistoryOpen(false)} className="border-slate-200">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
